import pkceChallenge from 'react-native-pkce-challenge';
import { getKey, hasKey } from '@bonanza-social/i18n';
import { Toast } from '@bonanza-social/moonlight';
import {
  State,
  createSlice,
  injectSlice,
  persistReducer,
  createAsyncThunk,
  appAuthenticatedTransitionEnd,
} from '@bonanza-social/toolkit/state';
import { ApiError } from '@bonanza-social/toolkit/api';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import { BottomSheet } from '@bonanza-social/navigation';
import {
  BiometricsState,
  SetBiometryTypeAction,
  SetPromptShownAction,
} from './biometrics.types';
import { getToken, setLoggedIn } from '../auth/auth.slice';
import { clientId } from '../auth/auth.constants';
import BiometricsService from '../../services/BiometricsService/BiometricsService';
import Biometrics from '../../utils/biometrics';

export const enableBiometrics = createAsyncThunk(
  'biometrics/enable',
  async () => {
    const { success } = await Biometrics.verifySimple();

    if (!success) {
      throw new Error('BIOMETRICS_SIMPLE_VERIFICATION_FAILURE');
    }

    const { publicKey, accountId } = await Biometrics.enable();
    const { credentialId } = await BiometricsService.enable({
      data: { publicKey },
    });

    return { accountId, credentialId };
  },
);

export const disableBiometrics = createAsyncThunk(
  'biometrics/disable',
  async (_, thunkApi) => {
    const credentialId = selectBiometricsCredentialId(
      <State>thunkApi.getState(),
    );

    if (credentialId !== undefined) {
      await BiometricsService.disable({
        pathParams: { credentialId },
      });
    }

    await Biometrics.disable();
  },
);

export const verifyBiometrics = createAsyncThunk(
  'biometrics/verify',
  async (_, thunkApi) => {
    const accountId = selectBiometricsAccountId(<State>thunkApi.getState());
    const credentialId = selectBiometricsCredentialId(
      <State>thunkApi.getState(),
    );

    if (!accountId) {
      return;
    }

    const { challenge } = await BiometricsService.start({
      pathParams: { accountId },
    });
    const { signature, success } = await Biometrics.verify(challenge);
    const { codeChallenge, codeVerifier } = pkceChallenge();

    const finishVerification = async ({ acceptTc }: { acceptTc: boolean }) => {
      try {
        const { authorizationCode } = await BiometricsService.finish({
          data: {
            signature,
            credentialId,
            codeChallenge,
            acceptTc,
            clientId,
            scope: 'openid profile bonanzasocial-api offline_access',
            redirectUri: 'bonanzasocial://callback',
          },
          pathParams: {
            accountId,
          },
          headers: {
            'X-Fit-To-Play': false,
          },
        });

        await getToken({
          grant_type: 'authorization_code',
          redirect_uri: 'bonanzasocial://callback',
          code: authorizationCode,
          code_verifier: codeVerifier,
        });

        thunkApi.dispatch(setLoggedIn(true));
      } catch (error) {
        if (error instanceof ApiError) {
          const { errorCode, errorMessages } = error?.data || {};

          // TODO: Replace with proper error code once BE is updated
          if (errorMessages?.[0] === 'Terms and conditions not accepted.') {
            await new Promise((resolve, reject) => {
              BottomSheet.open({
                namespace: 'auth',
                type: 'TermsAndConditions',
                data: {
                  confirm: resolve,
                  onClose: reject,
                },
              });
            });

            await finishVerification({ acceptTc: true });

            return;
          }

          const errorMessage =
            errorCode && hasKey(`auth.errors.${errorCode}`)
              ? getKey(`auth.errors.${errorCode}`)
              : errorMessages?.[0] || '';

          if (errorMessage) {
            setTimeout(() => {
              Toast.show({
                type: 'error',
                text: errorMessage,
              });
            }, 1000);
          }
        }

        throw error;
      }
    };

    if (success && signature) {
      await finishVerification({ acceptTc: false });
    }
  },
  {
    condition(_, { getState }) {
      const dataState = selectBiometricsDataState(<State>getState());

      return dataState !== 'pending';
    },
  },
);

const initialState: BiometricsState = {
  accountId: undefined,
  credentialId: undefined,
  biometryType: undefined,
  promptShown: false,
  dataState: 'idle',
};

const biometrics = createSlice({
  name: 'biometrics',
  initialState,
  reducers: {
    setBiometryType(state, { payload }: SetBiometryTypeAction) {
      state.biometryType = payload;
    },
    setPromptShown(state, { payload }: SetPromptShownAction) {
      state.promptShown = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(enableBiometrics.fulfilled, (state, { payload }) => {
      const { accountId, credentialId } = payload;

      state.accountId = accountId;
      state.credentialId = credentialId;
    });

    builder.addCase(disableBiometrics.fulfilled, (state) => {
      state.accountId = undefined;
      state.credentialId = undefined;
    });

    builder.addCase(verifyBiometrics.pending, (state) => {
      state.dataState = 'pending';
    });

    builder.addCase(verifyBiometrics.fulfilled, (state) => {
      state.dataState = 'fulfilled';
    });

    builder.addCase(verifyBiometrics.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(appAuthenticatedTransitionEnd, (state) => {
      state.dataState = 'idle';
    });
  },
});

const persistConfig = {
  key: 'biometrics',
  storage: AsyncStorage,
  blacklist: ['biometryType', 'dataState'],
};

const persistedReducer = persistReducer(persistConfig, biometrics.reducer);

export const withBiometrics = injectSlice({
  reducerPath: 'biometrics',
  reducer: persistedReducer,
});

export const { setBiometryType, setPromptShown } = biometrics.actions;

export const selectBiometricsEnabled = withBiometrics.selector(
  (state) =>
    state.biometrics.accountId !== undefined &&
    state.biometrics.credentialId !== undefined &&
    (!state.auth?.isLoggedIn ||
      selectBiometricsAccountId(state) === state.account?.data?.accountId),
);

export const selectBiometricsType = withBiometrics.selector(
  (state) => state.biometrics.biometryType,
);

export const selectBiometricsAccountId = withBiometrics.selector(
  (state) => state.biometrics.accountId,
);

export const selectBiometricsCredentialId = withBiometrics.selector(
  (state) => state.biometrics.credentialId,
);

export const selectBiometricsPromptShown = withBiometrics.selector(
  (state) => state.biometrics.promptShown,
);

export const selectBiometricsDataState = withBiometrics.selector(
  (state) => state.biometrics.dataState,
);

declare global {
  interface LazyLoadedSlices {
    biometrics: ReturnType<typeof persistedReducer>;
  }
}
